// Sun, ambient and the muzzle/explosion light, plus the shadow frustum fit to the map bounds. The map's own
// lamps are baked into the light volume (bakedLight.ts), so no real-time map point lights exist.

import * as THREE from "three";
import type { MapEnv } from "./mapTypes";
import type { QualityPreset } from "./quality";

/** Specular-only-ish contribution of the sky environment map (diffuse ambient comes from the hemisphere light). */
export const ENV_INTENSITY = 0.4;

const _corner = new THREE.Vector3();
const _center = new THREE.Vector3();

/**
 * Tight orthographic box (in light view space) around a world AABB, for a light looking along `-sunDir`.
 * Returns the shadow camera extents.
 */
export function fitShadowFrustum(
  camera: THREE.OrthographicCamera,
  sunPosition: THREE.Vector3,
  target: THREE.Vector3,
  bounds: THREE.Box3,
  margin = 2,
): void {
  camera.position.copy(sunPosition);
  camera.lookAt(target);
  camera.updateMatrixWorld(true);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < 8; i++) {
    _corner
      .set(i & 1 ? bounds.max.x : bounds.min.x, i & 2 ? bounds.max.y : bounds.min.y, i & 4 ? bounds.max.z : bounds.min.z)
      .applyMatrix4(camera.matrixWorldInverse);
    if (_corner.x < minX) minX = _corner.x;
    if (_corner.x > maxX) maxX = _corner.x;
    if (_corner.y < minY) minY = _corner.y;
    if (_corner.y > maxY) maxY = _corner.y;
    if (_corner.z < minZ) minZ = _corner.z;
    if (_corner.z > maxZ) maxZ = _corner.z;
  }
  camera.left = minX - margin;
  camera.right = maxX + margin;
  camera.bottom = minY - margin;
  camera.top = maxY + margin;
  // View space looks down -Z: nearer points have larger z.
  camera.near = Math.max(0.1, -maxZ - margin);
  camera.far = -minZ + margin;
  camera.updateProjectionMatrix();
}

export class SceneLighting {
  readonly hemi = new THREE.HemisphereLight(0xcfe0f2, 0x8d8c88, 1);
  readonly sun = new THREE.DirectionalLight(0xffe1b5, 3);
  /** Single dynamic light for muzzle flashes and explosions (always present so the shader stays stable). */
  readonly fx = new THREE.PointLight(0xffc880, 0, 20, 2);
  /** Direction TO the sun (world space, normalized). */
  readonly sunDir = new THREE.Vector3(0.4, 0.8, 0.3).normalize();
  private readonly scene: THREE.Scene;
  private bounds = new THREE.Box3(new THREE.Vector3(-50, 0, -50), new THREE.Vector3(50, 30, 50));
  private shadowsEnabled = true;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0003;
    this.sun.castShadow = true;
    this.fx.name = "fxLight";
    scene.add(this.hemi, this.sun, this.sun.target, this.fx);
  }

  /** Applies a map's environment colours and re-fits the shadow frustum to its bounds. */
  configure(env: MapEnv, bounds: THREE.Box3): void {
    this.hemi.color.set(env.ambientSky);
    this.hemi.groundColor.set(env.ambientGround);
    this.hemi.intensity = env.ambientIntensity;
    this.sun.color.set(env.sunColor);
    this.sun.intensity = env.sunIntensity;
    this.sunDir.set(env.sunDir[0], env.sunDir[1], env.sunDir[2]).normalize();
    this.bounds.copy(bounds);
    this.fit();
  }

  /** Re-fits the sun's shadow camera to the map bounds. */
  fit(): void {
    this.bounds.getCenter(_center);
    const radius = this.bounds.getBoundingSphere(new THREE.Sphere()).radius;
    this.sun.position.copy(_center).addScaledVector(this.sunDir, radius + 5);
    this.sun.target.position.copy(_center);
    this.sun.updateMatrixWorld(true);
    this.sun.target.updateMatrixWorld(true);
    const shadow = this.sun.shadow;
    fitShadowFrustum(shadow.camera, this.sun.position, _center, this.bounds);
    const size = shadow.mapSize.x;
    const texel = Math.max(shadow.camera.right - shadow.camera.left, shadow.camera.top - shadow.camera.bottom) / size;
    shadow.normalBias = Math.min(texel * 1.6, 0.25);
  }

  applyQuality(q: QualityPreset, shadows: boolean): void {
    this.shadowsEnabled = shadows;
    this.sun.castShadow = shadows;
    this.sun.shadow.radius = q.shadowRadius;
    if (this.sun.shadow.mapSize.x !== q.shadowMapSize) {
      this.sun.shadow.mapSize.set(q.shadowMapSize, q.shadowMapSize);
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
      this.fit();
    }
  }

  get shadows(): boolean {
    return this.shadowsEnabled;
  }

  dispose(): void {
    this.scene.remove(this.hemi, this.sun, this.sun.target, this.fx);
    this.sun.shadow.map?.dispose();
  }
}
